import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
  inject,
  OnInit,
  OnDestroy,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { DOCUMENT } from '@angular/common';

export type AppModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * AppModal - Componente de modal responsivo
 *
 * @example
 * ```html
 * <app-modal
 *   [isOpen]="showModal"
 *   size="lg"
 *   [closable]="true"
 *   (modalClosed)="handleClose()">
 *
 *   <app-modal-header>
 *     <h2>Título do Modal</h2>
 *   </app-modal-header>
 *
 *   <app-modal-content>
 *     Conteúdo do modal aqui
 *   </app-modal-content>
 *
 *   <app-modal-footer>
 *     <app-button (clicked)="handleSave()">Salvar</app-button>
 *     <app-button variant="ghost" (clicked)="handleClose()">Cancelar</app-button>
 *   </app-modal-footer>
 * </app-modal>
 * ```
 */
@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [NgClass],
  template: `
    @if (isOpenSignal()) {
    <div
      class="modal-overlay"
      [ngClass]="overlayClasses()"
      (click)="handleOverlayClick($event)"
      role="dialog"
      aria-modal="true"
      [attr.aria-labelledby]="titleId"
      [attr.aria-describedby]="contentId"
    >
      <div
        class="modal-container"
        [ngClass]="containerClasses()"
        (click)="$event.stopPropagation()"
      >
        @if (closable()) {
        <button
          type="button"
          class="modal-close"
          (click)="handleClose()"
          aria-label="Fechar modal"
        >
          <i class="icon-x" aria-hidden="true"></i>
        </button>
        }

        <div class="modal-content">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
    }
  `,
  styles: [
    `
      .modal-overlay {
        @apply fixed inset-0 z-50 
        flex items-center justify-center
        bg-black bg-opacity-50 backdrop-blur-sm
        p-4;
        animation: fadeIn 0.2s ease-out;
      }

      .modal-container {
        @apply relative bg-white rounded-lg shadow-strong
        max-h-full overflow-y-auto
        focus:outline-none;
        animation: slideUp 0.3s ease-out;
      }

      /* Sizes */
      .size-sm {
        @apply max-w-sm w-full;
      }

      .size-md {
        @apply max-w-md w-full;
      }

      .size-lg {
        @apply max-w-2xl w-full;
      }

      .size-xl {
        @apply max-w-4xl w-full;
      }

      .size-full {
        @apply max-w-none w-full h-full rounded-none;
      }

      .modal-close {
        @apply absolute top-4 right-4 z-10
        p-2 rounded-full
        text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100
        focus:outline-none focus:ring-2 focus:ring-primary-500
        transition-colors duration-150;
      }

      .modal-content {
        @apply p-6;
      }

      /* Animações */
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `,
  ],
  host: {
    '[style.pointer-events]': 'isOpenSignal() ? "auto" : "none"',
  },
})
export class AppModalComponent implements OnInit, OnDestroy {
  private document = inject(DOCUMENT);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);

  // Signals para estado
  isOpenSignal = signal(false);
  size = signal<AppModalSize>('md');
  closable = signal(true);
  closeOnOverlayClick = signal(true);

  @Input() titleId?: string;
  @Input() contentId?: string;

  // Enhanced inputs
  @Input({ required: false }) set isOpen(value: boolean) {
    this.isOpenSignal.set(value);
    this.handleOpenStateChange();
  }

  @Input({ required: false }) set modalSize(value: AppModalSize) {
    this.size.set(value);
  }

  @Input({ required: false }) set isClosable(value: boolean) {
    this.closable.set(value);
  }

  @Input({ required: false }) set closeOnOverlay(value: boolean) {
    this.closeOnOverlayClick.set(value);
  }

  @Output() modalClosed = new EventEmitter<void>();
  @Output() modalOpened = new EventEmitter<void>();

  // Computed values
  overlayClasses = computed(() => {
    return '';
  });

  containerClasses = computed(() => {
    return `size-${this.size()}`;
  });

  ngOnInit(): void {
    // Adicionar listener para ESC
    this.renderer.listen(this.document, 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.isOpenSignal() && this.closable()) {
        this.handleClose();
      }
    });
  }

  ngOnDestroy(): void {
    // Restaurar scroll quando componente for destruído
    this.renderer.removeClass(this.document.body, 'modal-open');
  }

  private handleOpenStateChange(): void {
    if (this.isOpenSignal()) {
      // Prevenir scroll da página
      this.renderer.addClass(this.document.body, 'modal-open');
      this.modalOpened.emit();

      // Focus no modal
      setTimeout(() => {
        const modalContainer =
          this.elementRef.nativeElement.querySelector('.modal-container');
        if (modalContainer) {
          modalContainer.focus();
        }
      });
    } else {
      // Restaurar scroll da página
      this.renderer.removeClass(this.document.body, 'modal-open');
    }
  }

  handleClose(): void {
    if (this.closable()) {
      this.isOpenSignal.set(false);
      this.handleOpenStateChange();
      this.modalClosed.emit();
    }
  }

  handleOverlayClick(event: MouseEvent): void {
    if (this.closeOnOverlayClick() && this.closable()) {
      this.handleClose();
    }
  }
}

/**
 * AppModalHeader - Cabeçalho do modal
 */
@Component({
  selector: 'app-modal-header',
  standalone: true,
  template: `
    <div class="modal-header">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      .modal-header {
        @apply mb-6 pb-4 border-b border-neutral-200;
      }

      :host ::ng-deep h1,
      :host ::ng-deep h2,
      :host ::ng-deep h3 {
        @apply text-xl font-semibold text-neutral-900 m-0;
      }
    `,
  ],
})
export class AppModalHeaderComponent {}

/**
 * AppModalContent - Conteúdo do modal
 */
@Component({
  selector: 'app-modal-content',
  standalone: true,
  template: `
    <div class="modal-content">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      .modal-content {
        @apply text-neutral-700 leading-relaxed;
      }
    `,
  ],
})
export class AppModalContentComponent {}

/**
 * AppModalFooter - Rodapé do modal
 */
@Component({
  selector: 'app-modal-footer',
  standalone: true,
  template: `
    <div class="modal-footer">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      .modal-footer {
        @apply mt-6 pt-4 border-t border-neutral-200 flex items-center justify-end gap-3;
      }
    `,
  ],
})
export class AppModalFooterComponent {}
